// API client for the non-streaming BFF endpoints.
//
//   POST /api/hero/action      — signup gate (deploy / migrate)
//   GET  /api/hero/providers   — provider list for input badges
//   GET  /api/hero/config      — single ResultCard by configId (carry-deploy)
//   GET  /api/hero/docs/content — doc-card excerpt by manifest slug (help entry)
//
// The streaming /api/hero/search lives in SSEClient — kept separate so
// each class has a focused surface area.

import type { ActionRequest, ActionResponse, ProvidersResponse, ResultCard } from '../types/index.js';
import { AuthError, NetworkError, NotFoundError, RateLimitError } from './errors.js';

/** Wire shape of GET /api/hero/docs/content (BFF handler_docs.go). `source`
 *  distinguishes a real article excerpt from the manifest blurb the BFF fell
 *  back to when the docs fetch failed. */
export interface DocExcerptResponse {
  slug: string;
  title: string;
  url: string;
  excerpt: string;
  description?: string;
  source: 'markdown' | 'description';
}

export interface ApiClientOptions {
  apiBase?: string;
  authToken?: string;
  fetchImpl?: typeof fetch;
}

export class ApiClient {
  private apiBase: string;
  private authToken: string | undefined;
  private fetchImpl: typeof fetch;

  constructor(opts: ApiClientOptions = {}) {
    this.apiBase = opts.apiBase ?? '';
    this.authToken = opts.authToken;
    this.fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  setAuthToken(token: string | undefined): void {
    this.authToken = token;
  }

  /**
   * POST /api/hero/action — signup gate. Returns the URL to redirect to.
   * The widget uses the synchronous `window.open` trick (T12) to avoid
   * popup blockers, so this method just resolves the URL — the host's
   * click handler does the navigation.
   */
  async postAction(req: ActionRequest, signal?: AbortSignal): Promise<ActionResponse> {
    const url = `${this.apiBase}/api/hero/action`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    let response: Response;
    try {
      response = await this.fetchImpl(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(req),
        signal,
      });
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') throw e;
      throw new NetworkError(`fetch failed: ${(e as Error).message}`);
    }

    return parseJsonResponse<ActionResponse>(response, '/api/hero/action');
  }

  /**
   * POST /api/hero/transcript — stash the verbatim chat transcript keyed by a
   * fresh nonce, so the portal can re-hydrate the conversation after signup
   * (dialog-carry Phase 1). Best-effort + fire-and-forget: uses `keepalive` so
   * the request survives the immediate navigation to the signup page.
   *
   * NOTE: `keepalive` caps the ENTIRE in-flight request body at 64KB and the
   * browser drops an oversized body before it leaves — so the caller
   * (captureTranscript) MUST front-truncate `turns` under that ceiling before
   * calling this; a body the BFF would have truncated never reaches the BFF on
   * this transport. The BFF still bounds the payload (defence-in-depth + the
   * non-keepalive path). Resolves on completion; caller ignores result/error.
   */
  async stashTranscript(key: string, turns: unknown[]): Promise<void> {
    const url = `${this.apiBase}/api/hero/transcript`;
    await this.fetchImpl(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key, turns }),
      keepalive: true,
    });
  }

  /**
   * POST /api/hero/event — fire-and-forget product-analytics beacon. Today it
   * carries the bottom-CTA click ("Continue with emma" on the landing /
   * "Deploy to project" in the portal); the BFF turns it into the bounded
   * counter `emma_chat_cta_clicks_total{surface,intent}`.
   *
   * `sessionId` + `turnIndex` (optional) correlate the click to the
   * conversation: the BFF writes a per-click `cta_click` log line keyed by
   * `sessionId`, which joins to that session's `query_analytics` lines →
   * per-session journey attribution (Phase 1a). Both are already in hand at the
   * call site (the dialog-carry POST sends the same pair); omitting them just
   * leaves the aggregate counter with no journey link.
   *
   * Transport is `fetch(keepalive)` — the SAME path as stashTranscript, which is
   * proven to deliver cross-origin from the marketing site (www.emma.ms → the
   * BFF on ai-chat.emma.ms). We deliberately do NOT use `navigator.sendBeacon`:
   * in the cross-origin landing embed it silently failed to fire (sendBeacon is
   * commonly neutered by privacy/ad blockers as a "tracking" API and is finicky
   * cross-origin), while the keepalive fetch went through — verified in the
   * browser Network tab (transcript delivered, beacon did not). keepalive lets
   * the POST survive the deploy-tab open. Body is a plain string sent as
   * `text/plain` (a "simple" CORS request → no preflight); the BFF decodes JSON
   * from the raw body. NEVER throws — analytics must never break the click.
   */
  sendCtaClick(surface: string, intent: string, sessionId?: string, turnIndex?: number): void {
    try {
      const url = `${this.apiBase}/api/hero/event`;
      const payload: Record<string, unknown> = { event: 'cta_click', surface, intent };
      if (sessionId) payload.sessionId = sessionId;
      if (typeof turnIndex === 'number') payload.turnIndex = turnIndex;
      const body = JSON.stringify(payload);
      void this.fetchImpl(url, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body,
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* best-effort: a failed beacon must never affect the click */
    }
  }

  /**
   * GET /api/hero/config — fetch a single ResultCard by emma SDK config
   * UUID. Carry-deploy entry: the portal's HeroDeployPage extracts
   * `workload`, `config` and the workload's narrow key from the
   * post-signup URL and calls this method to re-render the same card the
   * user picked on the landing.
   *
   * The narrow key differs by workload (the BFF reflects emma's filters):
   *   - vm / spot: `dataCenterId` (the only narrow; emma has no
   *     lookup-by-id, so the BFF walks that DC's configs).
   *   - kubernetes: `location` (numeric id) — /v1/kubernetes-configs has
   *     no dataCenterId filter, so the BFF scopes by locationId. (Phase 3a.)
   *
   * Throws:
   *   - NotFoundError on HTTP 404 (config no longer in emma SDK catalog).
   *     Caller renders an oob bubble offering refine.
   *   - NetworkError on 400 (invalid params), 5xx, transport errors.
   *
   * Path is built via URLSearchParams so spaces / unicode are encoded
   * once and only once.
   */
  async getConfig(
    workload: 'vm' | 'spot' | 'kubernetes',
    id: string,
    opts: {
      dataCenterId?: string;
      location?: string;
      acceleratorId?: string;
      k8sMode?: string;
      workerCount?: number;
      nodeMin?: number;
      nodeMax?: number;
    },
    signal?: AbortSignal,
  ): Promise<ResultCard> {
    const params = new URLSearchParams({ workload, id });
    if (opts.dataCenterId) params.set('dataCenterId', opts.dataCenterId);
    if (opts.location) params.set('location', opts.location);
    // GPU type UUID (vm/spot GPU configs) — narrows the BFF's server-side scan
    // so a valid config in a very large DC is reachable within the page cap.
    if (opts.acceleratorId) params.set('acceleratorId', opts.acceleratorId);
    // Phase 3b: preserve the K8s deploy mode so the re-fetched card keeps it.
    if (opts.k8sMode) params.set('k8sMode', opts.k8sMode);
    // Phase 3c: preserve the static worker-node count (BFF param key
    // k8sWorkerCount). Only send when >1 — the BFF defaults it to 1 when absent,
    // matching buildPostSignupRedirect's ">1 only" round-trip contract.
    if (opts.workerCount && opts.workerCount > 1) params.set('k8sWorkerCount', String(opts.workerCount));
    // ITEM-A: preserve a CUSTOM autoscale bracket (asg/both) so a non-default
    // range survives the signup redirect. Only send a valid max>min>0 pair; the
    // BFF re-derives the default 2–16 when absent.
    if (opts.nodeMin && opts.nodeMax && opts.nodeMax > opts.nodeMin) {
      params.set('nodeMin', String(opts.nodeMin));
      params.set('nodeMax', String(opts.nodeMax));
    }
    const url = `${this.apiBase}/api/hero/config?${params.toString()}`;

    const headers: Record<string, string> = {};
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    let response: Response;
    try {
      response = await this.fetchImpl(url, { method: 'GET', headers, signal });
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') throw e;
      throw new NetworkError(`fetch failed: ${(e as Error).message}`);
    }

    if (response.status === 404) {
      const body = await response.text().catch(() => '');
      throw new NotFoundError('/api/hero/config: config not found', body);
    }
    return parseJsonResponse<ResultCard>(response, '/api/hero/config');
  }

  /**
   * GET /api/hero/docs/content — short prose excerpt of a documentation page,
   * used to fill the doc-card body (<emma-chat>.showDocCard). The BFF proxies
   * the docs site because the widget can't reach it cross-origin; `slug` is
   * checked against the manifest allowlist there, never used as a URL.
   *
   * PROGRESSIVE, never load-bearing: the card is already on screen with the
   * host's manifest description when this runs, so every failure path (BFF
   * off, 404, rate limit, network) is handled by the CALLER simply keeping
   * what it has. No auth header — the endpoint is anonymous by design (public
   * docs content); sending the portal JWT would leak it to no purpose.
   */
  async getDocExcerpt(slug: string, signal?: AbortSignal): Promise<DocExcerptResponse> {
    const params = new URLSearchParams({ slug });
    const url = `${this.apiBase}/api/hero/docs/content?${params.toString()}`;

    let response: Response;
    try {
      response = await this.fetchImpl(url, { method: 'GET', signal });
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') throw e;
      throw new NetworkError(`fetch failed: ${(e as Error).message}`);
    }

    if (response.status === 404) {
      const body = await response.text().catch(() => '');
      throw new NotFoundError('/api/hero/docs/content: unknown doc', body);
    }
    return parseJsonResponse<DocExcerptResponse>(response, '/api/hero/docs/content');
  }

  /**
   * GET /api/hero/providers — provider list for the badges row in
   * `<emma-chat-input>` (T07). No auth required (public endpoint).
   */
  async getProviders(signal?: AbortSignal): Promise<ProvidersResponse> {
    const url = `${this.apiBase}/api/hero/providers`;

    let response: Response;
    try {
      response = await this.fetchImpl(url, {
        method: 'GET',
        signal,
      });
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') throw e;
      throw new NetworkError(`fetch failed: ${(e as Error).message}`);
    }

    return parseJsonResponse<ProvidersResponse>(response, '/api/hero/providers');
  }
}

/**
 * Shared response handling: validates status, parses JSON, maps error
 * codes to typed error classes.
 */
async function parseJsonResponse<T>(response: Response, path: string): Promise<T> {
  if (response.status === 429) {
    const body = await response.text().catch(() => '');
    const retryAfter = response.headers.get('Retry-After');
    const seconds = retryAfter ? parseInt(retryAfter, 10) : undefined;
    throw new RateLimitError(
      `${path}: rate limited`,
      body,
      Number.isFinite(seconds) ? seconds : undefined,
    );
  }
  if (response.status === 401 || response.status === 403) {
    const body = await response.text().catch(() => '');
    throw new AuthError(
      `${path}: ${response.status === 401 ? 'unauthorized' : 'forbidden'}`,
      response.status,
      body,
    );
  }
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new NetworkError(`${path}: HTTP ${response.status}`, response.status, body);
  }
  try {
    return (await response.json()) as T;
  } catch (e) {
    throw new NetworkError(`${path}: failed to parse JSON: ${(e as Error).message}`);
  }
}
